import { AnimationEvent } from "@angular/animations";
import { BreakpointState } from "@angular/cdk/layout";
import { AsyncPipe, NgIf } from "@angular/common";
import {
    Component,
    ElementRef,
    EventEmitter,
    Inject,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewChild,
} from "@angular/core";
import { ReactiveFormsModule, ControlContainer } from "@angular/forms";

import { Subscription } from "rxjs";

import { getExpandCollapseAnimation } from "@hermes/aphrodite/animations";
import { BaseInputComponent } from "@hermes/aphrodite/fields";
import { LayoutFacade } from "@hermes/aphrodite/layout";
import { SeparatorComponent } from "@hermes/aphrodite/separator";

import { WINDOW } from "@hermes/app-core";
import { SrOnlyNoSnippetDirective } from "@hermes/utils-generic/directives/sr-only-no-snippet";
import { BreakpointService } from "@hermes/utils-generic/services/user-interface";

/**
 * This component implements a global radio button Component.
 *
 * @see https://angular.io/guide/forms-overview#common-form-foundation-classes
 *
 * You must use this input with a FormGroup : `formControlName` must have the name of the AbstractControl.
 *
 * The value of the `name` must equal the value of the `radioButtonControl`
 *
 * You can also collapse and display content when a radio button is clicked
 *
 * If you want to not collapse a content and not display a separator, you need to add to
 * the component `[isCollapsible]=false` and `[needSeparator]=false`.
 *
 * Usage:
 * ```
 *  <h-radio-button
 *      [id]="'field-id'"
 *      [name]="'field'"
 *      [formControl]="'control'"
 *      i18n-label="@@my.key.translate"
 *      label="Name radio button"
 *      describe="'My describe'"
 *      (radioCheckedEvent)="radioCheckedEvent($event)">
 *
 *          <p>This is my first choice</p>
 *          <ng-container subLabel>
 *              <p>Content in subLabel</p>
 *          </ng-container>
 *          <ng-container rightContent>
 *              <p>Content to the right</p>
 *          </ng-container>
 *
 *  </h-radio-button>
 * ```
 *
 */
export const LIGHT = "light";
export const BOLD = "bold";
export const NORMAL = "normal";
export type LabelStyle = typeof LIGHT | typeof BOLD | typeof NORMAL;

export const THICK = "thick";
export const MEDIUM = "medium";
export const THIN = "thin";
export type ButtonStyle = typeof THICK | typeof MEDIUM | typeof THIN;

@Component({
    selector: "h-radio-button",
    templateUrl: "./radio-button.component.html",
    styleUrls: ["./radio-button.component.scss"],
    animations: [getExpandCollapseAnimation],
    imports: [
        AsyncPipe,
        NgIf,
        SeparatorComponent,
        ReactiveFormsModule,
        SrOnlyNoSnippetDirective,
    ],
    standalone: true,
})
export class RadioButtonComponent<T = unknown>
    extends BaseInputComponent
    implements OnInit, OnDestroy
{
    @ViewChild("radioButtonChecked")
    public radioButtonClicked!: ElementRef;

    @Input()
    public value!: T;

    /**
     * Used when a content is injected inside a radio button
     */
    @Input()
    public isCollapsible = true;

    /**
     * If you need a separator to be displayed between your
     * radio button and the collapsed content
     */
    @Input()
    public hasSeparator = true;

    @Input()
    public dataTestId?: string;

    @Input()
    public labelledById?: string;

    @Input()
    public labelStyle: LabelStyle = BOLD;

    @Input()
    public buttonStyle: ButtonStyle = MEDIUM;

    @Input()
    public isAnimationDisabled: boolean = true;

    @Input()
    public isScrollToView = false;

    @Input()
    public animationDuration: number = 500;

    @Input()
    public isFlexWrapContainer = false;

    @Output()
    public radioCheckedEvent: EventEmitter<{
        selected: T;
        activeElementId: string;
    }> = new EventEmitter();

    private isMobile: boolean | undefined;
    private subscription: Subscription = new Subscription();

    constructor(
        protected override controlContainer: ControlContainer,
        public layoutFacade: LayoutFacade,
        @Inject(WINDOW) private window: Window,
        private breakpointService: BreakpointService,
    ) {
        super(controlContainer);
    }

    public ngOnInit(): void {
        this.subscription.add(
            this.breakpointService
                .mediumBreakpointObserver()
                .subscribe((value: BreakpointState) => {
                    this.isMobile = !value.matches;
                    this.isAnimationDisabled =
                        this.isMobile && this.isScrollToView;
                }),
        );
    }

    public ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }

    public toggleRadioButton(value: T): void {
        this.radioCheckedEvent.emit({
            selected: value,
            activeElementId: this.id,
        });
    }

    public scrollToView(event: AnimationEvent): void {
        if (
            event.toState === "open" &&
            this.isScrollToView &&
            this.radioButtonClicked.nativeElement &&
            this.isMobile
        ) {
            const rec =
                this.radioButtonClicked.nativeElement.getBoundingClientRect();
            const offset = 30;
            this.window.scrollTo({
                top: this.window.scrollY + rec.top - offset,
                behavior: "smooth",
            });
        }
    }
}
